import type { ColDef, ColGroupDef } from '@ag-grid-community/core';

import PerformanceDashboardGroupHeaderRenderer from '@/components/performance-dashboard/PerformanceDashboardGroupHeaderRenderer.vue';
import TableDateCell from '@/components/shared/table-cells/TableDateCell.vue';
import TableOwnerCell from '@/components/shared/table-cells/TableOwnerCell.vue';
import TablePartnerCell from '@/components/shared/table-cells/TablePartnerCell.vue';
import TableSelectAllHeader from '@/components/shared/table-cells/TableSelectAllHeader.vue';
import TableTagsCell from '@/components/shared/table-cells/TableTagsCell.vue';

import { ALL_SEGMENTS, SEGMENTS_TO_LABEL_MAP } from '@/types/populations';

import PartnerTabSharingLevelPlaceholderCell from '../cells/PartnerTabSharingLevelPlaceholderCell.vue';
import type { SharingPartnerRow } from '../types';

export const defaultColumnDefinition: ColDef<SharingPartnerRow> = {
  flex: 1,
  sortable: false,
  suppressHeaderMenuButton: true,
  suppressSpanHeaderHeight: true,
};

function buildSegmentColumns(
  side: 'incoming' | 'outgoing',
): ColDef<SharingPartnerRow>[] {
  return ALL_SEGMENTS.map((segment, index) => ({
    colId: `${side}_${segment}`,
    headerName: SEGMENTS_TO_LABEL_MAP[segment],
    cellRenderer: PartnerTabSharingLevelPlaceholderCell,
    cellRendererParams: { segmentKey: segment, side },
    minWidth: 110,
    headerClass:
      index === ALL_SEGMENTS.length - 1
        ? ['segment-header', 'border-end']
        : 'segment-header',
    cellClass: index === ALL_SEGMENTS.length - 1 ? 'border-end' : undefined,
  }));
}

export function usePartnerTabColumnDefinitions(): (
  | ColDef<SharingPartnerRow>
  | ColGroupDef<SharingPartnerRow>
)[] {
  return [
    {
      field: 'partner',
      headerName: 'Partner',
      headerComponent: TableSelectAllHeader,
      headerComponentParams: { selectAllLabel: 'Select all partners' },
      cellRenderer: TablePartnerCell,
      flex: 2,
      minWidth: 260,
      pinned: 'left',
      lockPosition: 'left',
      headerClass: 'border-end',
      cellClass: 'border-end',
    },
    {
      headerName: 'Partner Populations',
      marryChildren: true,
      headerGroupComponent: PerformanceDashboardGroupHeaderRenderer,
      headerGroupComponentParams: {
        textPlain: 'Partner Populations',
        tooltipText:
          'The sharing level each partner has set for their populations with you.',
      },
      children: buildSegmentColumns('incoming'),
    },
    {
      headerName: 'My Populations',
      marryChildren: true,
      headerGroupComponent: PerformanceDashboardGroupHeaderRenderer,
      headerGroupComponentParams: {
        textPlain: 'My Populations',
        tooltipText:
          'The sharing level you have set for each of your populations with this partner.',
      },
      children: buildSegmentColumns('outgoing'),
    },
    {
      field: 'owner',
      headerName: 'Owner',
      cellRenderer: TableOwnerCell,
      minWidth: 140,
    },
    {
      field: 'tags',
      headerName: 'Tags',
      cellRenderer: TableTagsCell,
      flex: 2,
      minWidth: 140,
    },
    {
      field: 'lastActivity',
      headerName: 'Last Activity',
      cellRenderer: TableDateCell,
      minWidth: 100,
    },
    {
      field: 'lastSync',
      headerName: 'Last Sync',
      cellRenderer: TableDateCell,
      minWidth: 100,
    },
  ];
}
